<template>
  <a-modal
    destroyOnClose
    :maskClosable="false"
    :visible="visible"
    width="450px"
    :footer="null"
    centered
    @cancel="handleCloseModal"
  >
    <div class="container__inactive">
      <img class="image__delete" :src="deleteImage" alt="delete" />
      <h1 class="text__alert">
        ¿Estás seguro de eliminar este
        {{ isIdentifier ? "identificador" : "canal" }} de
        {{ record?.channel_name }} asociado a {{ record?.associate }}?
      </h1>
    </div>
    <p class="text">Recuerda que se tomarán las siguientes acciones:</p>
    <ul>
      <li>{{ firstText }}</li>
      <li>{{ secondText }}</li>
      <li 
        v-if="
          (record.channel_id === ChannelType.Whatsapp && record.data_parameters.enable_voice) || 
          record.channel_id === ChannelType.Email"
      >
        {{ thirdText }}
      </li>
    </ul>
    
    <section v-if="record.channel_id === ChannelType.Whatsapp && record.data_parameters.enable_voice">
      <a-form class="form" layout="vertical">
        <a-row :gutter="24">
          <a-col span="24">
            <a-form-item class="form__item">
              <template #label>
                <span class="icon-required">*</span>
                <span> Escribe la palabra <span class="strong">"Eliminar"</span> </span>
              </template>
              <a-auto-complete
                :status="isTextIncorrect ? 'error' : ''"
                v-model:value="confirmText"
                placeholder="Escribe aquí..."
                :class="{ 'ant-form-item-has-error': isTextIncorrect }"
              />
              <p v-if="isTextIncorrect" class="form__error">Palabra inválida</p>
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </section>

    <section v-if="[ChannelType.Messenger, ChannelType.Instagram].includes(record.channel_id)">
      <div class="video-placeholder" />

      <iframe
        :src="modalSrc"
        title="Delete facebook channel button"
        id="delete-channel-iframe"
        name="delete-channel-iframe"
        height="32px"
        width="100%"
      />
    </section>

    <div class="button__disabled" v-else>
      <a-button 
        :style="buttonStyle"
        :disabled="toggleDisabledButton"
        class="footer__delete" 
        type="primary" 
        @click="fetchDelete"
      >
        Eliminar {{ isIdentifier ? "identificador" : "canal" }}
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import { defineComponent, ref } from "vue";
import { message, Modal } from "ant-design-vue";

import { tokenManager } from "@/app/shared/utils/tokenManager";
import { ChannelType } from "@/app/shared/utils/enums";
// import { MessengerManager } from "@/app/shared/utils/loadFacebook";
import deleteImage from "@/app/users/assets/profiles/delete.png";

const DeletePageUrl = {
  [ChannelType.Messenger]: "deleteFacebookPage",
  [ChannelType.Instagram]: "deleteInstagramPage",
};

export default defineComponent({
  name: "DeleteChannel",
  components: {},
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    record: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const confirmText = ref("");
    const isButtonDisabled = ref(false);
    return {
      confirmText,
      isButtonDisabled,
    };
  },
  data() {
    return {
      ChannelType,
      deleteImage,
      Voice: 7,
      SMS: 2,
      WhatsApp: 3,
      WebChat: 5,
      facebook: 6,
      modalSrc: "",
      listener: null,
    };
  },
  mounted() {
    const token = tokenManager.getToken();
    const workspace = localStorage.getItem("workspace");
    const hostname =
      window.location.hostname == "localhost" ? "localhost:8080" : import.meta.env.VITE_HOSTNAME;

    this.modalSrc = `https://${hostname}/${
      DeletePageUrl[this.record.channel_id]
    }?embed=true&access_token=${token}&workspace=${workspace}&channelId=${this.record.id}`;

    this.listener = (event) => {
      console.log(event);
      console.log(event.data);
      if (event.origin !== `https://${import.meta.env.VITE_HOSTNAME}`) return;
      if (event.data === "successDelete") return this.showMessage();
      if (event.data === "fbUserIdNotMatching")
        return this.openWrongMetaUserModal({ channel: "Facebook" });
      if (event.data === "igUserIdNotMatching")
        return this.openWrongMetaUserModal({ channel: "Instagram" });

      this.$message.error("Ha ocurrido un error, vuelva a intentarlo");
    };

    window.addEventListener("message", this.listener);
  },
  computed: {
    firstText() {
      if (this.record.channel_id === ChannelType.Whatsapp) {
        if (this.record.data_parameters.enable_voice) {
          return "Se eliminará el identificador de WhatsApp Voice asociado a este número."
        } else {
          return "Todas las interacciones asociadas únicamente a este identificador serán finalizadas."
        }
      }
      if (this.record.channel_id === ChannelType.WhatsappVoice) {
        return "Todas las interacciones asociadas únicamente a este identificador serán finalizadas."
      }
      return "Todas las interacciones asociadas unicamente a este canal serán finalizadas."
    },
    secondText() {
      if (this.record.channel_id === this.Voice) {
        return "Este identificador de voz será desactivado y todas las llamadas serán cortadas";
      }
      if (this.record.channel_id === this.SMS) {
        return "Este canal de SMS será desactivado";
      }
      if (this.record.channel_id === ChannelType.Whatsapp) {
        if (this.record.data_parameters.enable_voice) {
          return "Todas las interacciones asociadas a este identificador de WhatsApp Message y Voice serán finalizadas.";
        } else {
          return "Este identificador de WhatsApp Message se desvinculará de las colas en las que esté asociada."
        }
      }
      if (this.record.channel_id === this.WebChat) {
        return "Este canal de Web Chat se desactivará de las colas que estén asociadas.";
      }
      if (this.record.channel_id === this.facebook) {
        return "Este canal de Facebook Messenger se desactivará de las colas que estén asociadas.";
      }
      if (this.record.channel_id === ChannelType.Instagram) {
        return "Este canal de Instagram se desactivará de las colas que estén asociadas.";
      }
      if (this.record.channel_id === ChannelType.WhatsappVoice) {
        return "Este identificador de WhatsApp Voice se desvinculará de las colas e IVRs en las que esté asociado.";
      }
      return "Este canal se desactivará de las colas que estén asociadas.";
    },
    isIdentifier() {
      const id = this.record?.channel_id
      return [ChannelType.Whatsapp, ChannelType.WhatsappVoice].includes(id)
    },
    thirdText() {
      if (this.record.channel_id === ChannelType.Whatsapp) {
        return "Este identificador de WhatsApp Message y Voice se desvinculará de las colas o IVRs en las que esté asociada."
      }
      if (this.record.channel_id === ChannelType.Email) {
        return "Se descartarán todos los borradores que los usuarios tengan asociados a este identificador."
      }
    },
    isTextIncorrect() {
      return this.confirmText != "Eliminar" && this.confirmText.length > 0;
    },
    toggleDisabledButton() {
      if (this.record.channel_id === ChannelType.Whatsapp && this.record.data_parameters.enable_voice) {
        return this.confirmText !== 'Eliminar'
      }
      return false
    },
    buttonStyle() {
      const full = this.record.channel_id === ChannelType.Whatsapp && this.record.data_parameters.enable_voice
      return full ? { width: '100%' } : null
    },
  },
  methods: {
    async fetchDelete() {
      try {
        // if (this.record.channel_id === this.facebook && this.record.data_parameters) {
        //   await MessengerManager.unlinkPageFromApp({
        //     pageId: this.record.data_parameters.id,
        //     pageAccessToken: this.record.data_parameters.token,
        //   });
        // }
        if (this.record.provider_name === "Inbound") {
          await this.$store.dispatch("deleteInboundChat", this.record.data_parameters.token);
        }
        await this.$store.dispatch("deleteChannel", this.record.id);

        this.showMessage();
      } catch (error) {
        console.error(error);
        const errorMessage = error?.getErrorMessage?.() || "Ha courrido un error";

        message.warning(errorMessage);
      }
    },
    showMessage() {
      if (this.record.channel_id === this.SMS) {
        this.$message.success(
          `Se eliminó con éxito el canal SMS.`
        );
      }
      else if (this.record.channel_id === ChannelType.Whatsapp){
        if (this.record.data_parameters.enable_voice) {
          this.$message.success(
            `El identificador de WhatsApp Message y Voice ${this.record.associate} fue eliminado.`
          );
        } else {
          this.$message.success(
            `El identificador de WhatsApp Message ${this.record.associate} fue eliminado.`
          );
        }
      }
      else if (this.record.channel_id === ChannelType.WhatsappVoice){
        this.$message.success(
          `El identificador de WhatsApp Voice ${this.record.associate} fue eliminado.`
        );
      }
      else {
        this.$message.success(
          `Se eliminó el canal de ${this.record.provider_name} asociado a ${this.record.associate}.`
        );
      }
      this.handleCloseModal();
    },
    handleCloseModal() {
      window.removeEventListener("message", this.listener, false);
      this.$emit("reloadTable");
      this.$emit("onClose");
    },
    openWrongMetaUserModal({ channel } = {}) {
      Modal.error({
        title: `Solo el usuario de ${channel} asociado puede eliminar este canal`,
        content: `Hemos detectado que la cuenta de ${channel} que ingresaste, no es la misma con la que se asoció la página ${this.record.associate}. Solo la cuenta asociada del usuario de ${channel} ${this.record.data_parameters.fb_user.name} puede eliminar esta página.`,
        width: 430,
        centered: true,
      });
    },
  },
});
</script>

<style lang="sass" scoped>
.image__delete
  margin-bottom: 10px

.text__alert
  font-size: 18px
  font-weight: 600

.text
  margin-bottom: 20px

.button__disabled
  margin-top: 10px
  display: flex
  justify-content: center

.strong
  font-weight: 600

.footer__delete
  margin-bottom: 2px

.strong
  font-weight: 600

.container__inactive
  text-align: center

.video-placeholder
  aspect-ratio: 16/9
  width: 100%
  background-color: $gray-8

iframe
  margin-top: 1em
  border: none

.form__error
  color: $red-6
  margin-top: 5px
  margin-bottom: -30px
</style>
